import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "@/lib/api";
import { rate, STATUS_LABELS, statusColor, RATE_UNITS } from "@/lib/format";
import MediaUpload from "@/components/MediaUpload";
import ListToolbar from "@/components/ListToolbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ seller_role: "", size: "", surface: "", city: "" });
  const [sort, setSort] = useState("newest");
  const [edit, setEdit] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [create, setCreate] = useState(null);
  const [options, setOptions] = useState({ sizes: [], surfaces: [] });

  useEffect(() => {
    api.get("/cities?active=true").then(({ data }) => setCities(data));
    api.get("/admin/sellers").then(({ data }) => setSellers(data));
    api.get("/product-options").then(({ data }) => setOptions(data));
  }, []);

  const openCreate = () => setCreate({ rate_unit: "Box", status: "active", seller_id: "", size: "", surface: "", city: "" });

  const saveCreate = async () => {
    const req = ["seller_id", "size", "surface", "rate_value", "catalogue_image", "stock_image", "stock_video", "city"];
    for (const r of req) if (!create[r]) { toast.error("Select a seller, fill all fields and upload 2 images + 1 video"); return; }
    try {
      await api.post("/admin/products", {
        seller_id: create.seller_id, title: create.title || "", size: create.size, surface: create.surface,
        rate_value: Number(create.rate_value), rate_unit: create.rate_unit || "Box",
        catalogue_image: create.catalogue_image, stock_image: create.stock_image, stock_video: create.stock_video,
        city: create.city, locality: create.locality || "", stock_qty: Number(create.stock_qty) || 0, status: create.status,
      });
      toast.success("Product created");
      setCreate(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (tab === "pending") p.set("status", "pending_approval");
    else if (tab !== "all") p.set("status", tab);
    if (search) p.set("q", search);
    if (filters.seller_role) p.set("seller_role", filters.seller_role);
    if (filters.size) p.set("size", filters.size);
    if (filters.surface) p.set("surface", filters.surface);
    if (filters.city) p.set("city", filters.city);
    p.set("sort", sort);
    const { data } = await api.get(`/admin/products?${p.toString()}`);
    setItems(data);
  }, [tab, search, filters, sort]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const act = async (fn) => { try { await fn(); toast.success("Done"); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = (id) => { if (window.confirm("Delete this product permanently?")) act(() => api.delete(`/admin/products/${id}`)); };

  const saveEdit = async () => {
    try {
      const payload = {
        title: edit.title || "", size: edit.size, surface: edit.surface,
        rate_value: Number(edit.rate_value), rate_unit: edit.rate_unit || "Box",
        catalogue_image: edit.catalogue_image, stock_image: edit.stock_image, stock_video: edit.stock_video,
        city: edit.city, locality: edit.locality || "", stock_qty: Number(edit.stock_qty) || 0,
      };
      await api.put(`/admin/products/${edit.id}`, payload);
      toast.success("Product updated");
      setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Products</h1>
          <p className="text-muted-foreground text-sm font-medium">Create, approve, edit and delete all platform products.</p>
        </div>
        <Button data-testid="admin-add-product-btn" onClick={openCreate} className="font-bold gap-1"><Plus className="h-4 w-4" /> Add Product</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="products-tab-all">All</TabsTrigger>
          <TabsTrigger value="pending" data-testid="products-tab-pending">Pending Approval</TabsTrigger>
          <TabsTrigger value="active" data-testid="products-tab-active">Active</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="products-tab-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <ListToolbar
        placeholder="Search product name / ID…"
        search={search} setSearch={setSearch}
        filters={[
          { key: "seller_role", label: "Seller Type", value: filters.seller_role, options: [{ value: "manufacturer", label: "Manufacturer" }, { value: "wholesaler", label: "Wholesaler" }, { value: "retailer", label: "Retailer" }] },
          { key: "size", label: "Size", value: filters.size, options: options.sizes.map((s) => ({ value: s, label: s })) },
          { key: "surface", label: "Surface", value: filters.surface, options: options.surfaces.map((s) => ({ value: s, label: s })) },
          { key: "city", label: "City", value: filters.city, options: cities.map((c) => ({ value: c.name, label: c.name })) },
        ]}
        onFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        sort={sort} setSort={setSort}
        sorts={[{ value: "newest", label: "Newest" }, { value: "oldest", label: "Oldest" }, { value: "price_low", label: "Price ↑" }, { value: "price_high", label: "Price ↓" }, { value: "most_viewed", label: "Most Viewed" }]}
        onReset={() => { setSearch(""); setFilters({ seller_role: "", size: "", surface: "", city: "" }); setSort("newest"); }}
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-card border rounded-xl overflow-hidden">
            <div className="relative aspect-video bg-secondary">
              <img src={fileUrl(p.catalogue_image)} alt={p.title} className="h-full w-full object-cover" />
              <Badge className={`absolute top-2 left-2 border ${statusColor(p.status)}`}>{STATUS_LABELS[p.status]}</Badge>
              <Badge className="absolute top-2 right-2 bg-black/70 text-white border-0 capitalize">{p.seller_role}</Badge>
            </div>
            <div className="p-3">
              <h3 className="font-heading font-bold text-sm line-clamp-1">{p.title}</h3>
              <div className="text-xs text-muted-foreground">{p.size} · {p.surface} · {p.city}</div>
              <div className="text-primary font-extrabold mt-1">{rate(p.rate_value, p.rate_unit)}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {p.status === "pending_approval" && (
                  <>
                    <Button size="sm" data-testid={`approve-${p.id}`} onClick={() => act(() => api.patch(`/admin/products/${p.id}/approve`))} className="gap-1 bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
                    <Button size="sm" variant="outline" data-testid={`reject-${p.id}`} onClick={() => act(() => api.patch(`/admin/products/${p.id}/reject`))} className="gap-1"><XCircle className="h-4 w-4 text-rose-600" /></Button>
                  </>
                )}
                {["pending_approval", "rejected"].includes(p.status) && (
                  <Button size="sm" variant="outline" data-testid={`edit-product-${p.id}`} onClick={() => setEdit({ ...p })} className="gap-1"><Pencil className="h-4 w-4" /> Edit</Button>
                )}
                <Button size="sm" variant="outline" data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-muted-foreground font-semibold">No products.</div>}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto ct-scroll">
          <DialogHeader><DialogTitle className="font-heading">Edit Product</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <img src={fileUrl(edit.catalogue_image)} alt="" className="w-full aspect-video object-cover rounded-lg border" />
              <div><Label>Title</Label><Input data-testid="edit-product-title" value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Size</Label>
                  <Select value={edit.size} onValueChange={(v) => setEdit({ ...edit, size: v })}>
                    <SelectTrigger data-testid="edit-product-size"><SelectValue /></SelectTrigger>
                    <SelectContent>{options.sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Surface</Label>
                  <Select value={edit.surface} onValueChange={(v) => setEdit({ ...edit, surface: v })}>
                    <SelectTrigger data-testid="edit-product-surface"><SelectValue /></SelectTrigger>
                    <SelectContent>{options.surfaces.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Rate (₹)</Label><Input data-testid="edit-product-rate" type="number" value={edit.rate_value} onChange={(e) => setEdit({ ...edit, rate_value: e.target.value })} /></div>
                <div><Label>Unit</Label>
                  <Select value={edit.rate_unit || "Box"} onValueChange={(v) => setEdit({ ...edit, rate_unit: v })}>
                    <SelectTrigger data-testid="edit-product-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{RATE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Stock (Boxes)</Label><Input data-testid="edit-product-qty" type="number" value={edit.stock_qty} onChange={(e) => setEdit({ ...edit, stock_qty: e.target.value })} /></div>
                <div><Label>City</Label>
                  <Select value={edit.city} onValueChange={(v) => setEdit({ ...edit, city: v })}>
                    <SelectTrigger data-testid="edit-product-city"><SelectValue /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Locality / Stock Location</Label><Input data-testid="edit-product-locality" value={edit.locality || ""} onChange={(e) => setEdit({ ...edit, locality: e.target.value })} /></div>
              <Button data-testid="save-product-edit" onClick={saveEdit} className="w-full font-bold">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!create} onOpenChange={(v) => !v && setCreate(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto ct-scroll">
          <DialogHeader><DialogTitle className="font-heading">Add Product</DialogTitle></DialogHeader>
          {create && (
            <div className="space-y-3">
              <div><Label>Seller</Label>
                <Select value={create.seller_id} onValueChange={(v) => setCreate({ ...create, seller_id: v })}>
                  <SelectTrigger data-testid="create-product-seller"><SelectValue placeholder="Select seller" /></SelectTrigger>
                  <SelectContent>{sellers.map((s) => <SelectItem key={s.id} value={s.id}>{s.company_name} · {s.role} · {s.mobile}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input data-testid="create-product-title" value={create.title || ""} onChange={(e) => setCreate({ ...create, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Size</Label>
                  <Select value={create.size} onValueChange={(v) => setCreate({ ...create, size: v })}>
                    <SelectTrigger data-testid="create-product-size"><SelectValue placeholder="Size" /></SelectTrigger>
                    <SelectContent>{options.sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Surface</Label>
                  <Select value={create.surface} onValueChange={(v) => setCreate({ ...create, surface: v })}>
                    <SelectTrigger data-testid="create-product-surface"><SelectValue placeholder="Surface" /></SelectTrigger>
                    <SelectContent>{options.surfaces.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Rate (₹)</Label><Input data-testid="create-product-rate" type="number" value={create.rate_value || ""} onChange={(e) => setCreate({ ...create, rate_value: e.target.value })} /></div>
                <div><Label>Unit</Label>
                  <Select value={create.rate_unit || "Box"} onValueChange={(v) => setCreate({ ...create, rate_unit: v })}>
                    <SelectTrigger data-testid="create-product-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{RATE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Stock (Boxes)</Label><Input data-testid="create-product-qty" type="number" value={create.stock_qty || ""} onChange={(e) => setCreate({ ...create, stock_qty: e.target.value })} /></div>
                <div><Label>City</Label>
                  <Select value={create.city} onValueChange={(v) => setCreate({ ...create, city: v })}>
                    <SelectTrigger data-testid="create-product-city"><SelectValue placeholder="City" /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Locality / Stock Location</Label><Input data-testid="create-product-locality" value={create.locality || ""} onChange={(e) => setCreate({ ...create, locality: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <MediaUpload testId="create-catalogue" label="Catalogue" kind="image" value={create.catalogue_image} onChange={(v) => setCreate({ ...create, catalogue_image: v })} />
                <MediaUpload testId="create-stock-image" label="Stock Image" kind="image" value={create.stock_image} onChange={(v) => setCreate({ ...create, stock_image: v })} />
                <MediaUpload testId="create-stock-video" label="Stock Video" kind="video" value={create.stock_video} onChange={(v) => setCreate({ ...create, stock_video: v })} />
              </div>
              <div><Label>Status</Label>
                <Select value={create.status} onValueChange={(v) => setCreate({ ...create, status: v })}>
                  <SelectTrigger data-testid="create-product-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="pending_approval">Pending Approval</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
              <Button data-testid="save-product-create" onClick={saveCreate} className="w-full font-bold">Create Product</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
